import type { WirePlan, WireTransaction } from '../api/types.ts';
import { t } from '../i18n/index.ts';

export type AskView = 'home' | 'forecast' | 'goals' | 'whatIf' | 'plan';

/** Matches a currency amount such as £500K, $1,200 or €3.5m. */
export const CURRENCY_AMOUNT = /[£$€]\s?\d[\d,.]*\s?(?:[kKmMbB](?![a-z]))?/g;

/** A plan label without the money in it ("£500K net worth" → "net worth"), so a prompt never quotes an amount. */
export function withoutAmounts(label: string): string {
  return label.replace(CURRENCY_AMOUNT, '').replace(/\s{2,}/g, ' ').trim();
}

const active = (tx: WireTransaction) => tx.isActive !== false;
const named = (label: string | undefined) => withoutAmounts(label ?? '');

function northStar(plan: WirePlan): string | null {
  const goals = [...(plan.goals ?? [])].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  const label = named(goals[0]?.label);
  return label || null;
}

/**
 * Three example questions for the user's assistant on this screen, phrased from the plan's own
 * items. They never carry the plan's figures: the assistant reads those itself, at the current version.
 */
export function assistantPrompts(plan: WirePlan, view: AskView): string[] {
  const txs = (plan.transactions ?? []).filter(active);
  const debts = txs.filter((tx) => tx.type === 'debt');
  const mortgage = debts.find((tx) => tx.linkedAssetId || /mortgage/i.test(tx.label ?? ''));
  const otherDebt = debts.find((tx) => tx !== mortgage);
  const investment = txs.find((tx) => tx.type === 'investment');
  const hasRent = txs.some((tx) => tx.type === 'expense' && /rent/i.test(tx.label ?? ''));
  const goal = northStar(plan);
  switch (view) {
    case 'home':
      return [
        goal ? t('ask.home.northStar', { goal }) : t('ask.home.overall'),
        t('ask.home.sinceLastMonth'),
        debts.length > 0 ? t('ask.home.debtFree') : t('ask.home.focus'),
      ];
    case 'forecast':
      return [
        otherDebt ? t('ask.forecast.debtFree') : mortgage ? t('ask.forecast.mortgageFree') : t('ask.forecast.speedUp'),
        t('ask.forecast.retire60'),
        otherDebt || mortgage ? t('ask.forecast.speedUp') : t('ask.forecast.tenYears'),
      ];
    case 'goals': {
      const second = named([...(plan.goals ?? [])].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))[1]?.label);
      return [
        t('ask.goals.furthest'),
        second ? t('ask.goals.sooner', { goal: second }) : goal ? t('ask.goals.sooner', { goal }) : t('ask.goals.suggest'),
        t('ask.goals.realistic'),
      ];
    }
    case 'whatIf':
      return [
        mortgage ? t('ask.whatIf.overpay', { debt: named(mortgage.label).toLowerCase() || t('ask.whatIf.mortgage') })
          : otherDebt ? t('ask.whatIf.overpay', { debt: named(otherDebt.label).toLowerCase() || t('ask.whatIf.debt') })
          : t('ask.whatIf.saveMore'),
        investment ? t('ask.whatIf.investMore', { item: named(investment.label) || t('ask.whatIf.investments') }) : t('ask.whatIf.investStart'),
        t('ask.whatIf.car'),
      ];
    case 'plan':
      return [
        hasRent || mortgage ? t('ask.plan.addPhone') : t('ask.plan.addRent'),
        t('ask.plan.statement'),
        t('ask.plan.missing'),
      ];
  }
}
